import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Info, Moon, Sun, X, FileText, Loader2, Minimize2 } from "lucide-react";
import { PDFDocument } from "pdf-lib";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd, DesktopOnlyAd } from "../components/AdPlacement";

function CompressPdf() {
  const [file, setFile] = useState(null);
  const [originalSize, setOriginalSize] = useState(0);
  const [compressedSize, setCompressedSize] = useState(0);
  const [compressedBlob, setCompressedBlob] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [quality, setQuality] = useState([70]);
  const [isDragging, setIsDragging] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Compress PDF - Reduce PDF File Size - KayTool";
  }, []);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setOriginalSize(selectedFile.size);
      setCompressedBlob(null);
      setCompressedSize(0);
    } else {
      toast.error("Please select a valid PDF file");
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile(droppedFile);
      setOriginalSize(droppedFile.size);
      setCompressedBlob(null);
      setCompressedSize(0);
    } else {
      toast.error("Please drop a valid PDF file");
    }
  };

  const compressPdf = async () => {
    if (!file) {
      toast.error("Please select a PDF file first");
      return;
    }

    setIsProcessing(true);
    setProgress(0);

    try {
      const arrayBuffer = await file.arrayBuffer();
      setProgress(20);

      // Load the PDF
      const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
      setProgress(40);

      // Get all pages and optimize
      const pages = pdfDoc.getPages();
      
      // Remove metadata to reduce size
      pdfDoc.setTitle('');
      pdfDoc.setAuthor('');
      pdfDoc.setSubject('');
      pdfDoc.setKeywords([]);
      pdfDoc.setProducer('');
      pdfDoc.setCreator('');
      setProgress(60);

      // Save with compression options
      const compressedBytes = await pdfDoc.save({
        useObjectStreams: true,
        addDefaultPage: false,
        objectsPerTick: 50,
      });
      setProgress(90);

      const blob = new Blob([compressedBytes], { type: 'application/pdf' });
      setCompressedBlob(blob);
      setCompressedSize(blob.size);
      setProgress(100);

      const reduction = ((originalSize - blob.size) / originalSize * 100).toFixed(1);
      if (blob.size < originalSize) {
        toast.success(`PDF compressed! Reduced by ${reduction}%`);
      } else {
        toast.info("PDF is already optimized. Minimal compression achieved.");
      }
    } catch (error) {
      console.error("Compression error:", error);
      toast.error("Failed to compress PDF: " + error.message);
    }

    setIsProcessing(false);
  };

  const downloadCompressed = () => {
    if (!compressedBlob) return;
    const url = URL.createObjectURL(compressedBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name.replace('.pdf', '_compressed.pdf');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const clearFile = () => {
    setFile(null);
    setOriginalSize(0);
    setCompressedSize(0);
    setCompressedBlob(null);
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-red-50 to-orange-50'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
              <Minimize2 className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Info className="w-3 h-3" /> 100% Local
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-full mb-4">
            <Minimize2 className="w-5 h-5" />
            <span className="font-medium">Compress PDF</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Compress PDF Files
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Reduce PDF file size while maintaining quality. Fast and secure compression.
          </p>
        </div>

        {/* Upload Area */}
        <Card 
          className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 max-w-2xl mx-auto ${
            isDragging ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-slate-300 dark:border-slate-600'
          }`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <CardContent className="p-12 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
            {!file ? (
              <div 
                className="cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-zone"
              >
                <Upload className="w-16 h-16 mx-auto mb-4 text-red-500" />
                <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                  Drop PDF or click to upload
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  Select a PDF file to compress
                </p>
              </div>
            ) : (
              <div>
                <FileText className="w-16 h-16 mx-auto mb-4 text-red-500" />
                <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                  {file.name}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 mb-4">
                  Original size: {formatSize(originalSize)}
                </p>
                <Button variant="outline" size="sm" onClick={clearFile}>
                  <X className="w-4 h-4 mr-1" /> Remove
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {file && (
          <div className="max-w-2xl mx-auto mt-6 space-y-6">
            {isProcessing && (
              <Card className="dark:bg-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-2">
                    <Loader2 className="w-5 h-5 animate-spin text-red-500" />
                    <span className="text-sm text-slate-600 dark:text-slate-300">
                      Compressing PDF...
                    </span>
                  </div>
                  <Progress value={progress} className="h-3" />
                </CardContent>
              </Card>
            )}

            {!compressedBlob && !isProcessing && (
              <div className="text-center">
                <Button 
                  onClick={compressPdf}
                  size="lg"
                  className="bg-gradient-to-r from-red-500 to-red-600 hover:opacity-90"
                >
                  <Minimize2 className="w-5 h-5 mr-2" />
                  Compress PDF
                </Button>
              </div>
            )}

            {compressedBlob && (
              <Card className="dark:bg-slate-800">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-800 dark:text-white mb-4">Compression Results</h3>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="p-4 bg-slate-100 dark:bg-slate-700 rounded-lg text-center">
                      <p className="text-sm text-slate-500 dark:text-slate-400">Original</p>
                      <p className="text-2xl font-bold text-slate-800 dark:text-white">{formatSize(originalSize)}</p>
                    </div>
                    <div className="p-4 bg-green-100 dark:bg-green-900/30 rounded-lg text-center">
                      <p className="text-sm text-green-600 dark:text-green-400">Compressed</p>
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">{formatSize(compressedSize)}</p>
                    </div>
                  </div>
                  <div className="text-center mb-4">
                    <Badge variant="secondary" className="text-lg px-4 py-2">
                      {((originalSize - compressedSize) / originalSize * 100).toFixed(1)}% smaller
                    </Badge>
                  </div>
                  <Button 
                    onClick={downloadCompressed}
                    className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:opacity-90"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Download Compressed PDF
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default CompressPdf;
